import type { TimeUpdateEvent } from '@u-wave/react-vimeo';
import classNames from 'classnames';
import type { VideoPlayer } from 'cloudinary-video-player';
import type { CSSProperties, PropsWithChildren } from 'react';
import React, {
  lazy,
  Suspense,
  use,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Helmet from 'react-helmet';

import FadedImage, {
  FadeInEffectOption,
  RoundedCornersOptions,
} from 'src/components/common/faded-image';
import HlsVideo from 'src/components/common/video/hls-video';
import type { ContainerSize } from 'src/components/common/video/video-helper';
import {
  calculateScaleForDifferentAspectRatio,
  createVideoWrapperStyles,
} from 'src/components/common/video/video-helper';
import { SiteContext } from 'src/context/site-context';
import {
  CLOUDINARY_CLOUD_NAME,
  getBynderAdaptiveBitrateStreamingVideoUrl,
  getCloudinaryAdaptiveBitrateStreamingVideoUrl,
} from 'src/helpers/hosted-asset-helpers';
import imageFade from 'src/helpers/image-fades';
import { matchReducedMotion, useMatchMedia } from 'src/styling/media-queries';

import throttleToAnimationFrames from '../../../helpers/throttle-to-animation-frames';

import * as styles from './video-with-placeholder-image.module.scss';

const Vimeo = lazy(() => import('@u-wave/react-vimeo'));

type CloudinaryVideoPlayer = VideoPlayer & {
  on: (event: string, callback: () => void) => void;
};

export enum VideoType {
  BYNDER = 'Bynder',
  CLOUDINARY = 'Cloudinary',
  VIMEO = 'Vimeo',
}

export type VideoDetails = CloudinaryOrBynderVideoDetails | VimeoVideoDetails;

type CloudinaryOrBynderVideoDetails = {
  type: VideoType.CLOUDINARY | VideoType.BYNDER;
  videoId?: null;
  videoUrl: string;
  videoPlaceholder: Queries.ImageWithCropDetails | Queries.ImageDetails;
};

export type VimeoVideoDetails = {
  type: VideoType.VIMEO;
  videoId: string;
  videoUrl?: null;
  videoPlaceholder?: Queries.ImageWithCropDetails | Queries.ImageDetails;
};

export type VideoWithPlaceholderImageProps = PropsWithChildren<{
  video: VideoDetails;
  autoplay?: boolean;
  loop?: boolean;
  background?: boolean;
  muted?: boolean;
  disableAutopause?: boolean;
  forcePause?: boolean;
  onTimeUpdate?: (event: TimeUpdateEvent | null) => void;
  fadeType?: string;
  fadeDivClassName?: string;
  videoProgress?: number | null;
  isReady?: boolean;
}>;

const VideoWithPlaceholderImage = ({
  video,
  autoplay,
  loop,
  background,
  muted: mutedProp,
  disableAutopause,
  forcePause,
  onTimeUpdate,
  fadeType,
  fadeDivClassName,
  isReady = true,
  children,
  videoProgress,
}: VideoWithPlaceholderImageProps) => {
  const muted = mutedProp ?? autoplay;
  const prefersReducedMotion = useMatchMedia(matchReducedMotion, false);
  const [containerSize, setContainerSize] = useState<ContainerSize | null>(null);
  const [videoWrapperScale, setVideoWrapperScale] = useState<string | undefined>(undefined);
  const [videoOpacity, setVideoOpacity] = useState(video.type === VideoType.BYNDER ? 0 : 1);
  const containerRef = useRef<HTMLDivElement>(null);
  const cloudinaryVideoRef = useRef<CloudinaryVideoPlayer | null>(null);
  const [isVideoReady, setVideoReady] = useState(video.type !== VideoType.CLOUDINARY);
  const [isVimeoVideoPaused, setIsVimeoVideoPaused] = useState(true);
  const id = useId();
  const { isMeridian } = use(SiteContext);

  useEffect(
    function initialiseCloudinaryVideo() {
      if (video.type !== VideoType.CLOUDINARY || !video.videoUrl || !isReady) {
        return;
      }

      Promise.all([
        import('cloudinary-video-player'),
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- This is the correct import
        // @ts-ignore-next-line
        import('cloudinary-video-player/cld-video-player.min.css'),
      ])
        .then(([Cloudinary]) => {
          if (cloudinaryVideoRef.current) {
            setVideoReady(false);
            cloudinaryVideoRef.current.source(
              getCloudinaryAdaptiveBitrateStreamingVideoUrl(video.videoUrl) ?? ''
            );
            return;
          }

          const cloudinaryVideo = Cloudinary.videoPlayer(id, {
            cloudName: CLOUDINARY_CLOUD_NAME,
            autoplay: autoplay && !prefersReducedMotion,
            muted,
            loop,
            controls: false,
            bigPlayButton: false,
            playsinline: true,
            pictureInPictureToggle: false,
            fontFace: 'inherit',
          }) as CloudinaryVideoPlayer;

          cloudinaryVideo.source(
            getCloudinaryAdaptiveBitrateStreamingVideoUrl(video.videoUrl) ?? ''
          );

          const updateInterval = () => {
            const currentTime = cloudinaryVideo.currentTime() as number;
            const duration = cloudinaryVideo.duration();

            onTimeUpdate?.({
              duration: duration,
              seconds: currentTime,
              percent: currentTime / duration,
            });
          };

          cloudinaryVideo.on('loadeddata', () => setVideoReady(true));
          cloudinaryVideo.on('timeupdate', updateInterval);
          cloudinaryVideo.on('sourcechanged', () => onTimeUpdate?.(null));
          cloudinaryVideoRef.current = cloudinaryVideo;
        })
        // eslint-disable-next-line no-console
        .catch(console.error);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO why are the dependencies not exhaustive?
    [isReady]
  );

  useLayoutEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }

    const updateContainerSize = () =>
      setContainerSize({
        width: containerElement.offsetWidth,
        height: containerElement.offsetHeight,
      });

    updateContainerSize();

    const { throttled: onResize, cancel: cancelPendingResize } =
      throttleToAnimationFrames(updateContainerSize);
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(containerElement);
    return () => {
      resizeObserver.disconnect();
      cancelPendingResize();
    };
  }, []);

  const [prevVideoUrl, setPrevVideoUrl] = useState(video.videoUrl);
  if (prevVideoUrl !== video.videoUrl) {
    setPrevVideoUrl(video.videoUrl);
    if (video.type === VideoType.BYNDER) {
      setVideoOpacity(0);
    }
  }

  useEffect(
    function pauseOrPlayCloudinaryVideo() {
      if (video.type !== VideoType.CLOUDINARY) {
        return;
      }

      if ((autoplay ? !forcePause : forcePause) || !cloudinaryVideoRef.current) {
        return;
      }

      const videoElement = cloudinaryVideoRef.current;

      if (autoplay) {
        videoElement.pause();
      } else {
        videoElement.play();
      }

      return () => {
        if (autoplay) {
          void videoElement.play();
        } else {
          void videoElement.pause();
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO why are the dependencies not exhaustive?
    [forcePause, video.type]
  );

  const recalculateVideoWrapperScale = useCallback(() => {
    if (!containerSize) {
      return;
    }
    setVideoWrapperScale(
      calculateScaleForDifferentAspectRatio(containerRef.current, containerSize) ?? undefined
    );
  }, [containerSize]);

  useEffect(() => {
    recalculateVideoWrapperScale();
  }, [recalculateVideoWrapperScale, isVideoReady]);

  const videoStyle: CSSProperties = {
    ...createVideoWrapperStyles(containerSize, videoWrapperScale),
    opacity: isVideoReady ? 1 : 0,
  };

  const fadeDivBackground = isVideoReady
    ? `linear-gradient(${fadeType || imageFade.fullFade})`
    : undefined;

  const isVimeoPlayer = video.type === VideoType.VIMEO && !!video.videoId;

  const placeholderImage = (
    <FadedImage
      fixedImage={false}
      image={video.videoPlaceholder}
      fadeInEffect={FadeInEffectOption.NONE}
      loadingSkeleton={false}
      roundedCorners={isMeridian ? RoundedCornersOptions.NO : undefined}
      // Autoplay is enabled for hero videos, optimise their placeholder images:
      enablePreload={autoplay}
      enableBlurredPlaceholder={autoplay}
      loading={autoplay ? 'eager' : 'lazy'}
      imageWidthLimit={
        (!!video.videoUrl || !!video.videoId) && autoplay
          ? video.type === VideoType.BYNDER
            ? 1200 // Bynder videos  tend to start later (but at a higher quality), so the placeholder image is on screen for longer
            : 800
          : undefined
      }
    />
  );

  return (
    <div className={styles.wrapper} onMouseOver={() => setIsVimeoVideoPaused(false)}>
      <div className={styles.container} ref={containerRef}>
        <div className={styles.placeholderWrapper} aria-hidden="true">
          {placeholderImage}
        </div>
        <div className={styles.videoWrapper} style={videoStyle}>
          {video.type === VideoType.CLOUDINARY && !!video.videoUrl && (
            <>
              <Helmet>
                <link
                  rel="preload"
                  fetchPriority="high"
                  as="video"
                  href={getCloudinaryAdaptiveBitrateStreamingVideoUrl(video.videoUrl) ?? ''}
                />
              </Helmet>
              <video
                data-testid="cloudinary-video"
                id={id}
                className="cld-video-player cld-fluid"
              />
            </>
          )}
          {video.type === VideoType.BYNDER && !!video.videoUrl && (
            <>
              <Helmet>
                <link
                  rel="preload"
                  fetchPriority="high"
                  as="video"
                  href={getBynderAdaptiveBitrateStreamingVideoUrl(video.videoUrl) ?? ''}
                />
              </Helmet>
              <HlsVideo
                id={id}
                isReady={isReady}
                className={styles.video}
                style={{
                  opacity: videoOpacity,
                }}
                src={getBynderAdaptiveBitrateStreamingVideoUrl(video.videoUrl) ?? undefined}
                loop={loop}
                muted={muted}
                controls={false}
                forcePause={forcePause}
                autoPlay={autoplay && !prefersReducedMotion}
                onVideoSizeChange={recalculateVideoWrapperScale}
                onTimeUpdate={(event) => {
                  const videoElem = event.currentTarget;
                  if (!videoElem.paused) {
                    setVideoOpacity(1);
                  }

                  onTimeUpdate?.({
                    duration: videoElem.duration,
                    seconds: videoElem.currentTime,
                    percent: videoElem.currentTime / videoElem.duration,
                  });
                }}
              />
            </>
          )}
          {isVimeoPlayer && (
            <Suspense fallback={null}>
              <Vimeo
                className={styles.vimeoPlayer}
                video={video.videoId}
                paused={(forcePause === undefined && isVimeoVideoPaused) || forcePause}
                loop={loop}
                muted={muted}
                controls={false}
                responsive={true}
                autopause={!disableAutopause}
                background={background}
                autoplay={autoplay && !prefersReducedMotion}
                onTimeUpdate={onTimeUpdate}
              />
            </Suspense>
          )}
        </div>

        {/* For hiding vimeo image placeholder if video is paused by default */}
        {videoProgress === null && isVimeoPlayer && (
          <div className={styles.placeholderWrapper} aria-hidden="true">
            {placeholderImage}
          </div>
        )}
      </div>
      <div
        className={classNames(styles.fadeDiv, fadeDivClassName)}
        style={{ background: fadeDivBackground }}
      >
        {children}
      </div>
    </div>
  );
};

export default VideoWithPlaceholderImage;
